/**
 * W4b (field test 2026-10-17) — W5 interactions the scheduling side owns. Pure.
 *
 * 1. AutoBump covered only the FIRST ticket of a technician's queue. A multi-bay technician (W5) has several live
 *    vehicles at once — one hands-on, others switched away from or running a machine process — and the ones that
 *    were not first never had their finish maintained, so the lane under-reserved them. `liveTicketFinishIntent`
 *    names the server finish intent for EVERY live ticket; the anchor's running/paused cases stay with AutoBump's
 *    existing branches (chain handling), so only what was uncovered is added. The server (ticket-finish-intent)
 *    decides on the database row: a paused SWITCHED vehicle or one with an open unattended operation is derived as
 *    "the technician returns, then the hands-on work that remains" — never "now + the rest" on top of the vehicle
 *    the technician is actually working on.
 * 2. The header workload of a multi-bay technician is the hands-on work that REMAINS: OptiTime minus the work recorded
 *    in timer sessions (OCT28 WP-2b-2b-i). Unattended / switched-away / paused / parked time is not labor, and work
 *    already done is not still to do.
 *
 * Rollover chain rows are never touched here: the chain authority (and, for an approved conversion, the server
 * allocation) owns their future.
 */
import { isTicketParked } from '../../../supabase/functions/_shared/schedulingParticipation';
import { workedMinutes } from '../../../supabase/functions/_shared/finishIntentDerivation';

export type LiveFinishIntent = 'run_long' | 'pause_extend' | 'unattended_hold';

export interface LiveTicketLike {
  status?: string | null;
  timer_status?: string | null;
  actually_started?: boolean | null;
  bay_id?: string | null;
  parking_lot?: boolean | null;
  parking_spot_id?: string | null;
  rollover_of_ticket_id?: string | null;
  parent_ticket_id?: string | null;
  rollover_conversion_id?: string | null;
}

export function isChainRelated(t: LiveTicketLike): boolean {
  return !!(t.rollover_of_ticket_id || t.parent_ticket_id || t.rollover_conversion_id);
}

export function liveTicketFinishIntent(t: LiveTicketLike, isAnchor: boolean): LiveFinishIntent | null {
  if (isChainRelated(t)) return null;
  if (t.status !== 'Active' && t.status !== 'Paused') return null;
  if (t.timer_status === 'running') return isAnchor ? null : 'run_long';
  // OCT28 WP-1 (Bucket 3 :600): paused work moved into parking accrues no pause pressure (schedulingParticipation).
  if (t.timer_status === 'paused') return isAnchor || isTicketParked(t) ? null : 'pause_extend';
  // Started, timer stopped, vehicle in a bay: a machine process may be running on it (the server checks the open
  // unattended operation and answers NOT_APPLICABLE when there is none).
  if (t.actually_started === true && !!t.bay_id) return 'unattended_hold';
  return null;
}

export interface WorkloadTicketLike {
  actually_started?: boolean | null;
  timer_status?: string | null;
}

/** One recorded work session (ticket_timer_sessions): the authoritative evidence of hands-on technician work. */
export interface WorkSessionLike {
  technician_id?: string | null;
  started_at: string | null;
  stopped_at: string | null;
  duration_min: number | null;
}

/** The ticket's recorded work sessions, or 'unavailable' when they could not be read (never "no work"). */
export type WorkEvidence = readonly WorkSessionLike[] | 'unavailable';

/**
 * OCT28 WP-2b-2b-i — remaining hands-on minutes for one ticket: its OptiTime minus the work actually RECORDED on it.
 *
 *   unstarted                  → its OptiTime (future planned work; no evidence needed)
 *   started, sessions read     → OptiTime − worked, worked = finishIntentDerivation.workedMinutes (the server's own
 *                                measure: closed sessions' duration_min + each open session's minutes up to now)
 *   started, sessions unread   → null (unknown — never 0 and never a guess)
 *
 * Before, hands-on so far was "wall time since the actual start − accumulated_bump_minutes". That treated a
 * scheduling-accounting counter as a labor record: it was only right while that counter happened to hold every unworked
 * minute, and parked hours, nights and breaks became "hands-on" whenever it did not (e.g. before the next bump — the
 * GAPS-4 clamp — or once the counter holds eligible pause minutes only, WP-2b-2b-ii). Sessions are written only while
 * someone works, so parked, paused, overnight and break time are never labor here.
 *
 * Work by EVERY technician on the repair counts (a helper's work is work no longer remaining), summed in person-minutes
 * exactly as the server's AutoBump planner does; sessions awaiting job allocation review are still work performed.
 * Never negative; a finished-early ticket contributes 0, not a credit.
 */
export function remainingHandsOnMinutes(t: WorkloadTicketLike, techOptiTime: number, nowMs: number, sessions: WorkEvidence): number | null {
  const opti = Number.isFinite(techOptiTime) && techOptiTime > 0 ? techOptiTime : 0;
  const started = t.actually_started === true || t.timer_status === 'running' || t.timer_status === 'paused';
  if (!started) return Math.round(opti);
  if (sessions === 'unavailable') return null;
  const worked = workedMinutes([...sessions], nowMs);
  return Math.max(0, Math.round(opti - worked));
}

export interface RemainingHandsOn {
  /** The sum over the tickets whose remaining work is known. */
  minutes: number;
  /** Started tickets whose work sessions could not be read: the header must not present `minutes` as the total. */
  unknownCount: number;
}

export function sumRemainingHandsOn(
  rows: Array<{ ticket: WorkloadTicketLike; techOptiTime: number; terminal?: boolean; sessions: WorkEvidence }>, nowMs: number,
): RemainingHandsOn {
  let minutes = 0;
  let unknownCount = 0;
  for (const r of rows) {
    if (r.terminal) continue;
    const m = remainingHandsOnMinutes(r.ticket, r.techOptiTime, nowMs, r.sessions);
    if (m == null) unknownCount += 1; else minutes += m;
  }
  return { minutes, unknownCount };
}

/**
 * NOV 3 (Decision 3, point 8) — the ELAPSED WORK the tech-column header feeds the canonical derived-time record.
 *
 * The header used wall time since `actual_start_time` as elapsed work, so an overnight break on a day still running
 * the next morning became hundreds of minutes of run-long (Adam F: workload 1,854). Elapsed work is the recorded work
 * sessions (finishIntentDerivation.workedMinutes — the server's own measure), exactly as the card and the server
 * read it:
 *   not started               → 0 s, known (nothing worked yet);
 *   started, sessions read     → worked minutes × 60, known;
 *   started, sessions unread   → 0 s, UNKNOWN (the record then derives no pause and no live run-long — never a guess).
 */
export function headerElapsedFromEvidence(
  t: WorkloadTicketLike, sessions: WorkEvidence, nowMs: number,
): { seconds: number; known: boolean } {
  const started = t.actually_started === true || t.timer_status === 'running' || t.timer_status === 'paused';
  if (!started) return { seconds: 0, known: true };
  if (sessions === 'unavailable') return { seconds: 0, known: false };
  return { seconds: Math.max(0, workedMinutes([...sessions], nowMs)) * 60, known: true };
}
